import { ShieldCheck } from "lucide-react";
import ResourcePage from "@/components/ResourcePage";
import { makeAdapter } from "@/lib/supabaseAdapter";

// Insurance is keyed by profile_name (no profile_id column in the existing schema).
const adapter = makeAdapter({ table: "insurance_info", filterColumn: "profile_name" });

export default function Insurance() {
  return (
    <ResourcePage
      config={{
        adapter,
        filterBy: "profile_name",
        eyebrow: "Coverage",
        title: "Insurance",
        description: "Keep policy details handy for every member of your family.",
        icon: ShieldCheck,
        accent: "bg-teal-light text-primary",
        addLabel: "Add insurance",
        testIdPrefix: "ins",
        blank: () => ({
          profile_id: "",
          provider_name: "",
          plan_name: "",
          policy_number: "",
          group_number: "",
          member_id: "",
          phone: "",
          effective_date: "",
          expiration_date: "",
          notes: "",
        }),
        transform: (f, { profile }) => ({
          // NOTE: schema has no profile_id on insurance_info — only profile_name
          profile_name: profile?.name || null,
          provider_name: f.provider_name,
          plan_name: f.plan_name || null,
          policy_number: f.policy_number || null,
          group_number: f.group_number || null,
          member_id: f.member_id || null,
          phone: f.phone || null,
          effective_date: f.effective_date || null,
          expiration_date: f.expiration_date || null,
          notes: f.notes || null,
        }),
        fields: [
          { key: "provider_name", label: "Provider", required: true, span: 2, placeholder: "Blue Cross" },
          { key: "plan_name", label: "Plan name", placeholder: "PPO" },
          { key: "member_id", label: "Member ID" },
          { key: "policy_number", label: "Policy #" },
          { key: "group_number", label: "Group #" },
          { key: "phone", label: "Member services phone" },
          { key: "effective_date", label: "Valid from", type: "date" },
          { key: "expiration_date", label: "Valid until", type: "date" },
          { key: "notes", label: "Notes", type: "textarea" },
        ],
        render: (i) => (
          <div>
            <div className="font-heading text-lg font-bold leading-tight">{i.provider_name}</div>
            {i.plan_name && (
              <div className="text-sm text-muted-foreground">{i.plan_name}</div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              {i.member_id && <Field label="Member ID" value={i.member_id} />}
              {i.policy_number && <Field label="Policy" value={i.policy_number} />}
              {i.group_number && <Field label="Group" value={i.group_number} />}
              {i.phone && <Field label="Phone" value={i.phone} />}
            </div>
            {(i.effective_date || i.expiration_date) && (
              <div className="mt-3 text-xs text-muted-foreground">
                Valid: {i.effective_date || "—"} → {i.expiration_date || "ongoing"}
              </div>
            )}
            {i.notes && (
              <div className="mt-3 text-xs text-muted-foreground">{i.notes}</div>
            )}
          </div>
        ),
      }}
    />
  );
}

const Field = ({ label, value }) => (
  <div className="rounded-lg bg-secondary/40 px-2.5 py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="truncate font-mono text-xs font-semibold">{value}</div>
  </div>
);
